import { ThemeItem } from "@features/api/openData";

interface NewsThemeItem {
  id: string;
  slug: string;
  name: string;
  sort: number;
}

/** 一级频道与下属子标签；channel 为接口下发的根主题 slug */
export interface NewsThemeGroup {
  channel: string;
  name: string;
  subTags: NewsThemeItem[];
}

export type ThemeRowWithRanking = ThemeItem & {
  parent_theme_id?: string;
  sort?: number | string | null;
};

/** News 一级频道排除列表（父主题 slug）；V2.7.2 新站隐藏 Crypto Tab */
const EXCLUDED_NEWS_PRIMARY_CHANNELS = ["crypto"] as const;

const toSortNumber = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER;
};

/**
 * 将 ranking themes 行整理为频道分组：根节点（无 parent_theme_id）为频道，子节点归入对应父级。
 * 是否在 UI / sitemap 展示由 {@link filterNewsThemeGroups} 决定。
 */
export function buildNewsThemeGroups(rows: ThemeItem[]): NewsThemeGroup[] {
  const rankingRows = rows as ThemeRowWithRanking[];
  const parentRows = rankingRows.filter(
    (row) =>
      row.parent_theme_id == null &&
      typeof row.slug === "string" &&
      row.slug.length > 0,
  );

  const childRows = rankingRows.filter(
    (row) => row.parent_theme_id != null && typeof row.slug === "string",
  );

  const childrenByParentId = new Map<string, ThemeRowWithRanking[]>();
  for (const child of childRows) {
    const parentId = child.parent_theme_id;
    if (!parentId) {
      continue;
    }
    const existing = childrenByParentId.get(parentId) || [];
    existing.push(child);
    childrenByParentId.set(parentId, existing);
  }

  return parentRows
    .sort((a, b) => toSortNumber(a.sort) - toSortNumber(b.sort))
    .map<NewsThemeGroup | null>((parent) => {
      if (!parent.id || !parent.slug) {
        return null;
      }

      const subTags = (childrenByParentId.get(parent.id) || [])
        .filter((child) => typeof child.slug === "string" && !!child.slug)
        .sort((a, b) => toSortNumber(a.sort) - toSortNumber(b.sort))
        .map((child) => ({
          id: child.id || child.slug!,
          slug: child.slug!,
          name: child.theme || child.slug!,
          sort: toSortNumber(child.sort),
        }));

      return {
        channel: parent.slug,
        name: parent.theme || parent.slug,
        subTags,
      };
    })
    .filter(
      (group): group is NewsThemeGroup => !!group && group.subTags.length > 0,
    );
}

/** 过滤一级 News 频道（顶栏 Tab、sitemap、/news 默认跳转等共用） */
export function filterNewsThemeGroups(
  groups: NewsThemeGroup[],
): NewsThemeGroup[] {
  const excluded = new Set<string>(EXCLUDED_NEWS_PRIMARY_CHANNELS);
  return groups.filter((group) => !excluded.has(group.channel));
}
